import * as yup from 'yup'

import { COLOR_VALUE, RECIPE_TYPE } from './constants'

export type FormData = {
  [key in FORM_FIELDS]: string
}

export const enum FORM_FIELDS {
  RECIPE_TYPE = 'recipe_type',
  RESTORATION_METHOD = 'restoration_method',
  PREPARATION_COLOR = 'prepcolor',
  PREPCOLOR_MIDDLE = 'prepcolor_middle',
  PREPCOLOR_CERVICAL = 'prepcolor_cervical',
  PREPCOLOR_INCISAL = 'prepcolor_incisal',
  TARGET_COLOR = 'targetcolor',
  TARGETMIDDLE = 'targetcolor_middle_value',
  TARGETCERVICAL = 'targetcolor_cervical_value',
  TARGETINCISAL = 'targetcolor_incisal_value',

  FRAMEWORK_OPTIONS = 'framework_thickness',
  FRAMEWORK_COLOR = 'framework_color',
  STAINING_BRAND = 'stainingbrand',
  BRAND = 'brand',
  AVAILABLE_SPACE = 'availspace',
  AVAILABLE_SPACE_MIDDLE = 'availspace_cervical',
  AVAILABLE_SPACE_CERVICAL = 'availspace_middle',
  AVAILABLE_SPACE_INCISAL = 'availspace_incisal',
  PREP_COLOR = 'prepColor',
}

// 🔹 Reusable 3-number array validator
const threeNumberArray = yup
  .array()
  .of(
    yup
      .number()
      .typeError('Must be a number')
      .required('Each value is required'),
  )
  .min(3, 'Must have 3 values')
  .max(3, 'Must have 3 values')

// 🔹 Helper to normalize recipeType from when()
const normalizeRecipeType = (raw: unknown) =>
  Array.isArray(raw) ? raw[0] : raw

// 🔹 Helper for prepcolor field validation (no object-style `when`)
const prepColorField = (label: string, alwaysRequired: boolean) =>
  yup.mixed().when(FORM_FIELDS.RECIPE_TYPE, function (rawRecipeType, schema) {
    const recipeType = normalizeRecipeType(rawRecipeType)

    // For TRIPLE_ZONE recipe type
    if (recipeType === RECIPE_TYPE.TRIPLE_ZONE) {
      return schema.test(
        `${label}-validation`,
        `${label} is required`,
        function (value) {
          const prepColorType = this.parent[FORM_FIELDS.PREP_COLOR]

          if (prepColorType === COLOR_VALUE.LabColor) {
            return (
              Array.isArray(value) &&
              value.length === 3 &&
              value.every((num) => {
                const n = Number(num)
                return !Number.isNaN(n)
              })
            )
          } else {
            return (
              value === '' ||
              (typeof value === 'string' && value.trim().length > 0)
            )
          }
        },
      )
    }

    // For NON–TRIPLE_ZONE recipe type
    if (alwaysRequired) {
      return schema.test(
        `${label}-validation`,
        `${label} is required`,
        function (value) {
          const prepColorType = this.parent[FORM_FIELDS.PREP_COLOR]

          if (prepColorType === COLOR_VALUE.LabColor) {
            return (
              Array.isArray(value) &&
              value.length === 3 &&
              value.every((num) => {
                const n = Number(num)
                return !Number.isNaN(n)
              })
            )
          } else {
            return typeof value === 'string' && value.trim().length > 0
          }
        },
      )
    }

    // Not required at all
    return schema.optional()
  })

// 🔹 Base required fields
const baseSchema = {
  [FORM_FIELDS.RECIPE_TYPE]: yup.string().required(`Recipe option is required`),

  [FORM_FIELDS.PREPARATION_COLOR]: yup.string().optional(),

  [FORM_FIELDS.FRAMEWORK_OPTIONS]: yup
    .string()
    .required(`Framework options is required`),

  [FORM_FIELDS.FRAMEWORK_COLOR]: yup
    .array()
    .of(yup.string().typeError('Must be a string').required())
    .required(`Framework color is required`),

  [FORM_FIELDS.STAINING_BRAND]: yup
    .string()
    .required(`Staining brand is required`),

  [FORM_FIELDS.BRAND]: yup.string().required(`Ceramic brand is required`),

  // Middle always required
  [FORM_FIELDS.TARGETMIDDLE]: threeNumberArray.required(
    'Target color middle value is required',
  ),

  [FORM_FIELDS.AVAILABLE_SPACE_MIDDLE]: yup
    .string()
    .required(`Available space middle is required`),

  // Prepcolor fields
  [FORM_FIELDS.PREPCOLOR_MIDDLE]: prepColorField(
    'Preparation middle color',
    true,
  ),
  [FORM_FIELDS.PREPCOLOR_CERVICAL]: prepColorField(
    'Preparation cervical color',
    false,
  ),
  [FORM_FIELDS.PREPCOLOR_INCISAL]: prepColorField(
    'Preparation incisal color',
    false,
  ),
}

const fieldMap = {
  [FORM_FIELDS.TARGETCERVICAL]: 'Target color cervical value',
  [FORM_FIELDS.TARGETINCISAL]: 'Target color incisal value',
  [FORM_FIELDS.AVAILABLE_SPACE_CERVICAL]: 'Available space cervical',
  [FORM_FIELDS.AVAILABLE_SPACE_INCISAL]: 'Available space incisal',
}

// 🔹 Region fields
const regionFields = [
  FORM_FIELDS.TARGETCERVICAL,
  FORM_FIELDS.TARGETINCISAL,
  FORM_FIELDS.AVAILABLE_SPACE_CERVICAL,
  FORM_FIELDS.AVAILABLE_SPACE_INCISAL,
]

// 🔹 Build region schema dynamically (with normalized recipeType)
const regionSchema = regionFields.reduce(
  (acc, field) => {
    if (
      field === FORM_FIELDS.TARGETCERVICAL ||
      field === FORM_FIELDS.TARGETINCISAL
    ) {
      acc[field] = threeNumberArray.when(
        FORM_FIELDS.RECIPE_TYPE,
        (rawRecipeType, schema: any) => {
          const recipeType = normalizeRecipeType(rawRecipeType)

          if (recipeType === RECIPE_TYPE.TRIPLE_ZONE) {
            return schema.required(
              `${fieldMap[field as keyof typeof fieldMap]} is required`,
            )
          }
          return schema.optional()
        },
      )
    } else {
      acc[field] = yup
        .string()
        .when(FORM_FIELDS.RECIPE_TYPE, (rawRecipeType, schema: any) => {
          const recipeType = normalizeRecipeType(rawRecipeType)

          if (recipeType === RECIPE_TYPE.TRIPLE_ZONE) {
            return schema.required(
              `${fieldMap[field as keyof typeof fieldMap]} is required`,
            )
          }
          return schema.optional()
        })
    }

    return acc
  },
  {} as Record<FORM_FIELDS, any>,
)

// 🔹 Final schema
export const validationSchema = yup.object().shape({
  ...baseSchema,
  ...regionSchema,
})
